import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Avatar,
  Badge,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ClientData } from '../../types/business';
import { useBusinessAgent } from '../../contexts/BusinessAgentContext';
import { analytics } from '../../services/analytics';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';

interface ClientsTableProps {
  clients: ClientData[];
  totalClients: number;
  isAddingClient: boolean;
  onAddClient: () => void;
  onEditClient: (client: ClientData) => void;
}

type SortBy = 'name' | 'totalHomes' | 'homesThisMonth' | 'activeHomes' | 'lastActivity';

const ClientActionsDropdown: React.FC<{ client: ClientData; onEdit: (client: ClientData) => void }> = ({ client, onEdit }) => {
  const navigate = useNavigate();
  const { deleteClient } = useBusinessAgent();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewProfileClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/business/agent/client/${encodeURIComponent(client.email)}`);
    handleMenuClose();
  };

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(client);
    handleMenuClose();
  };

  const handleUnlinkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setUnlinkDialogOpen(true);
    handleMenuClose();
  };

  const handleUnlinkConfirm = async () => {
    setError(null);
    setIsUnlinking(true);
    
    try {
      const result = await deleteClient(client.email);
      
      if (result.success) {
        setUnlinkDialogOpen(false);
      } else {
        setError(result.error || 'Failed to unlink client');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleUnlinkCancel = () => {
    setUnlinkDialogOpen(false);
    setError(null);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={isUnlinking}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Profile" />
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Client" />
        </MenuItem>
        <MenuItem onClick={handleUnlinkClick}>
          <ListItemIcon>
            <RemoveCircleOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Unlink Client" />
        </MenuItem>
      </Menu>

      {/* Unlink Confirmation Dialog */}
      <Dialog
        open={unlinkDialogOpen}
        onClose={!isUnlinking ? handleUnlinkCancel : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unlink Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unlink <strong>{client.name}</strong> ({client.email}) 
            from your client list? This will remove the client relationship but will not 
            delete their account.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 'medium', color: 'warning.main' }}>
            Note: The client will no longer be able to access your services through their 
            current relationship, but their account and data will remain intact.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleUnlinkCancel}
            disabled={isUnlinking}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUnlinkConfirm}
            color="error"
            variant="contained"
            disabled={isUnlinking}
            startIcon={isUnlinking ? <CircularProgress size={20} /> : <RemoveCircleOutlineIcon />}
          >
            {isUnlinking ? 'Unlinking...' : 'Unlink Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  totalClients,
  isAddingClient,
  onAddClient,
  onEditClient
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { business } = useBusiness();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortBy, setSortBy] = useState<SortBy>('name');

  // Log agent_client_list_open analytics event
  useAnalytics(
    () => {
      if (user?.uuid) {
        analytics.logAgentClientListOpen(
          user.uuid,
          totalClients,
          business?.id || null,
          business?.name || null
        );
      }
    },
    [user?.uuid, totalClients, business?.id, business?.name]
  );

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleRowClick = (client: ClientData) => {
    // Log agent_client_homes_open analytics event when viewing client homes
    if (user?.uuid && business?.id) {
      analytics.logAgentClientHomesOpen(
        business.id,
        user.uuid,
        client.uuid || client.email,
        client.totalHomes || 0
      );
    }
    navigate(`/business/agent/client/${encodeURIComponent(client.email)}`);
  };

  const sortedClients = [...clients].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'totalHomes':
        aValue = a.totalHomes;
        bValue = b.totalHomes;
        break;
      case 'homesThisMonth':
        aValue = a.homesThisMonth;
        bValue = b.homesThisMonth;
        break;
      case 'activeHomes':
        aValue = a.activeHomes;
        bValue = b.activeHomes;
        break;
      case 'lastActivity':
        aValue = new Date(a.lastActivity).getTime();
        bValue = new Date(b.lastActivity).getTime();
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderBottom: 1, 
        borderColor: 'divider' 
      }}>
        <Typography variant="h6">
          Clients ({totalClients})
        </Typography>
        <Button 
          startIcon={<PeopleAltIcon />} 
          variant="contained" 
          size="small"
          onClick={onAddClient}
          disabled={isAddingClient}
        >
          Add Client
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === 'totalHomes'}
                  direction={sortBy === 'totalHomes' ? sortOrder : 'asc'}
                  onClick={() => handleSort('totalHomes')}
                >
                  Total Homes
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === 'activeHomes'}
                  direction={sortBy === 'activeHomes' ? sortOrder : 'asc'}
                  onClick={() => handleSort('activeHomes')}
                >
                  Active Homes
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortBy === 'homesThisMonth'}
                  direction={sortBy === 'homesThisMonth' ? sortOrder : 'asc'}
                  onClick={() => handleSort('homesThisMonth')}
                >
                  This Month
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'lastActivity'}
                  direction={sortBy === 'lastActivity' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lastActivity')}
                >
                  Last Activity
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedClients.map((client) => (
              <TableRow 
                key={client.id} 
                hover 
                onClick={() => handleRowClick(client)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge
                      badgeContent={client.unreadMessages > 0 ? client.unreadMessages : null}
                      color="error"
                      max={99}
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{
                        mr: 2,
                        '& .MuiBadge-badge': {
                          fontSize: '0.75rem',
                          height: 20,
                          minWidth: 20,
                          borderRadius: '50%'
                        }
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {client.name.charAt(0)}
                      </Avatar>
                    </Badge>
                    {client.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                      <Link
                        href={`mailto:${client.email}`}
                        sx={{
                          color: 'inherit',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {client.email}
                      </Link>
                    </Typography>
                    {client.phone && (
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                        {client.phone}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">{client.totalHomes}</TableCell>
                <TableCell align="center">{client.activeHomes}</TableCell>
                <TableCell align="center">{client.homesThisMonth}</TableCell>
                <TableCell>
                  <Chip 
                    label={client.status} 
                    color={
                      client.status === 'active' ? 'success' : 
                      client.status === 'pending' ? 'warning' : 
                      client.status === 'inactive' ? 'error' : 
                      'default'
                    }
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>{client.lastActivity}</TableCell>
                <TableCell align="right">
                  <ClientActionsDropdown client={client} onEdit={onEditClient} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ClientsTable;